import { PRODUCT_PLACEHOLDERS } from "./product-placeholders";
// ---------------------------------------------------------------------------
// THREAD PRODUCTS — mirrored from supplier (Trivantage) for our own site.
//
// Trivantage's "thread" search returns 100+ SKUs that are mostly color and
// put-up variants of about half a dozen thread families. We mirror by family
// here (the way a customer actually shops) — each family card shows the most
// common color + size, and the detail page lists every color & put-up that
// family is available in.
//
// Each product has its own internal detail page at /shop/threads/$slug so
// customers stay on our site instead of being redirected.
// ---------------------------------------------------------------------------

export type ThreadVariant = {
  /** Item # from Trivantage catalog */
  itemNumber: string;
  color: string;
  size: string;
  /** Optional twist direction etc. */
  note?: string;
};

export type ThreadProduct = {
  id: string;
  slug: string;
  name: string;
  /** Headline size shown on the listing card (e.g. "Tex 90 · 8-oz spool") */
  size: string;
  /** Default item # (the most common color/size) shown on the card */
  itemNumber: string;
  /** Manufacturer / brand label */
  manufacturer: string;
  /** Short blurb shown on the listing card */
  blurb: string;
  /** Long description shown on the detail page (paragraphs) */
  description: string[];
  /** Bullet-point feature list */
  features: string[];
  /** Spec table key/value pairs */
  specs: { label: string; value: string }[];
  /** Product image URL (hosted by supplier CDN) */
  image: string;
  /** Available colors for this family (for the picker on the detail page) */
  colors: string[];
  /** Available sizes / put-ups for this family */
  sizes: string[];
  /** Sample variant SKUs — useful for the "available SKUs" callout */
  sampleVariants: ThreadVariant[];
  /** Optional note ("Special order — call to confirm color", etc.) */
  productNote?: string;
};

export const threadProducts: ThreadProduct[] = [
  {
    id: "premobond-bpt-92-tex-90",
    slug: "premobond-bpt-92-tex-90-bonded-polyester",
    name: "PremoBond® BPT Size 92 Bonded Polyester Thread",
    size: "Tex 90 · 8-oz & 16-oz Spools",
    itemNumber: "543010",
    manufacturer: "PremoBond",
    blurb:
      "Anti-wick bonded polyester — the everyday workhorse for awnings, marine canvas and outdoor upholstery.",
    description: [
      "PremoBond® BPT Size 92 (Tex 90) is a bonded polyester anti-wick thread that's the everyday workhorse of the awning and marine canvas trade. The bonded finish prevents fraying through industrial sewing machines, and the anti-wick treatment stops water from traveling along the seam — critical for awnings, boat covers and outdoor cushions.",
      "UV-stabilized for long outdoor life, color-fast and dimensionally stable. Sews cleanly on most walking-foot machines used by canvas shops, sail makers and upholsterers.",
      "Available in 12+ colors and in both 8-oz and 16-oz spools.",
    ],
    features: [
      "Tex 90 / V-92 — the most-used outdoor thread weight",
      "Bonded polyester finish — won't fray in industrial machines",
      "Anti-wick treatment — stops water from traveling along the seam",
      "UV-stable for awnings, boat covers and outdoor upholstery",
      "Sews on most walking-foot machines",
    ],
    specs: [
      { label: "Product Category", value: "Bonded Polyester Thread" },
      { label: "Size", value: "92 (Tex 90)" },
      { label: "Anti-Wick", value: "Yes" },
      { label: "Put Up", value: "8 oz. spool / 16 oz. spool" },
      { label: "Manufacturer", value: "PremoBond" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: [
      "White",
      "Black",
      "Navy",
      "Brown",
      "Burgundy",
      "Forest Green",
      "Olive Drab",
      "Steel Grey",
      "Sand",
      "Red",
    ],
    sizes: ["8-oz spool", "16-oz spool"],
    sampleVariants: [
      { itemNumber: "543010", color: "White", size: "16 oz" },
      { itemNumber: "543011", color: "Black", size: "16 oz" },
      { itemNumber: "543018", color: "Navy", size: "8 oz" },
      { itemNumber: "543019", color: "Brown", size: "8 oz" },
      { itemNumber: "543020", color: "Steel Grey", size: "8 oz" },
      { itemNumber: "543014", color: "Burgundy", size: "8 oz" },
      { itemNumber: "543015", color: "Forest Green", size: "8 oz" },
      { itemNumber: "543016", color: "Olive Drab", size: "8 oz" },
    ],
    productNote: "Tell us color + spool size in your order notes.",
  },
  {
    id: "premobond-bpt-138-tex-135",
    slug: "premobond-bpt-138-tex-135-bonded-polyester",
    name: "PremoBond® BPT Size 138 Bonded Polyester Thread",
    size: "Tex 135 · 8-oz & 16-oz Spools",
    itemNumber: "543030",
    manufacturer: "PremoBond",
    blurb:
      "Heavier Tex 135 anti-wick bonded polyester — for heavy canvas, vinyl and awning seam work.",
    description: [
      "PremoBond® BPT Size 138 (Tex 135) is the heavy-duty version of the BPT line. Same bonded polyester anti-wick formula, just thicker — used on heavy 18 oz vinyl, blackout canvas, banner work and high-stress awning seams.",
      "Most awning fabricators stock both BPT 92 and BPT 138 — the lighter weight for general canvas, the heavier weight for stress points and thick assemblies.",
      "Available in white right-twist as well as standard left-twist for two-needle machines.",
    ],
    features: [
      "Tex 135 / V-138 — heavy-duty outdoor weight",
      "Bonded polyester, anti-wick treatment",
      "UV-stable for awnings, banners, heavy vinyl",
      "Available in left- and right-twist (two-needle work)",
      "12+ colors in stock",
    ],
    specs: [
      { label: "Product Category", value: "Bonded Polyester Thread" },
      { label: "Size", value: "138 (Tex 135)" },
      { label: "Anti-Wick", value: "Yes" },
      { label: "Put Up", value: "8 oz. spool / 16 oz. spool" },
      { label: "Manufacturer", value: "PremoBond" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: [
      "White",
      "White (Right Twist)",
      "Black",
      "Navy",
      "Steel Grey",
      "Forest Green",
      "Olive Drab",
      "Sand",
      "Brown",
      "Red",
      "Burgundy",
    ],
    sizes: ["8-oz spool", "16-oz spool"],
    sampleVariants: [
      { itemNumber: "543030", color: "White", size: "16 oz" },
      { itemNumber: "543031", color: "Black", size: "16 oz" },
      { itemNumber: "543032", color: "White", size: "16 oz", note: "Right Twist" },
      { itemNumber: "543033", color: "Navy", size: "8 oz" },
      { itemNumber: "543035", color: "Steel Grey", size: "8 oz" },
      { itemNumber: "543036", color: "Forest Green", size: "8 oz" },
      { itemNumber: "543037", color: "Olive Drab", size: "8 oz" },
      { itemNumber: "543038", color: "Sand", size: "8 oz" },
      { itemNumber: "543039", color: "Brown", size: "8 oz" },
      { itemNumber: "543040", color: "Red", size: "8 oz" },
      { itemNumber: "543041", color: "Burgundy", size: "8 oz" },
    ],
    productNote: "Specify color, spool size and twist direction in order notes.",
  },
  {
    id: "premobond-bpt-69-tex-70",
    slug: "premobond-bpt-69-tex-70-bonded-polyester",
    name: "PremoBond® BPT Size 69 Bonded Polyester Thread",
    size: "Tex 70 · 16-oz Spool",
    itemNumber: "543003",
    manufacturer: "PremoBond",
    blurb:
      "Lighter Tex 70 bonded polyester — for upholstery, drapery and lightweight canvas.",
    description: [
      "PremoBond® BPT Size 69 (Tex 70) is the lighter sibling in the bonded polyester family. Used for indoor and outdoor upholstery, drapery, cushion covers and lightweight canvas where Tex 90 is overkill.",
      "Same bonded, anti-wick polyester formula — UV-stable, color-fast and won't fray in industrial machines.",
      "Sold in 16-oz spools.",
    ],
    features: [
      "Tex 70 / V-69 — lightweight upholstery / drapery weight",
      "Bonded polyester, anti-wick",
      "UV-stable, color-fast",
      "Industrial-grade, sews cleanly",
    ],
    specs: [
      { label: "Product Category", value: "Bonded Polyester Thread" },
      { label: "Size", value: "69 (Tex 70)" },
      { label: "Put Up", value: "16 oz. spool" },
      { label: "Manufacturer", value: "PremoBond" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: ["White", "Black", "Beige", "Navy", "Brown"],
    sizes: ["16-oz spool"],
    sampleVariants: [
      { itemNumber: "543003", color: "White", size: "16 oz" },
    ],
  },
  {
    id: "ae-anefil-nylon-bonded",
    slug: "ae-anefil-nylon-bonded-thread",
    name: "A&E Anefil® Nylon Bonded Thread",
    size: "Multiple Tex sizes · Spool",
    itemNumber: "AE-ANEFIL-NYLON",
    manufacturer: "American & Efird (A&E)",
    blurb:
      "Bonded nylon thread — high tensile strength for leather, heavy upholstery and indoor work.",
    description: [
      "A&E Anefil® bonded nylon is the go-to thread for leather, heavy upholstery, footwear, harness and bag work. Higher tensile strength than polyester at the same weight, with excellent abrasion resistance.",
      "Best for indoor or shaded applications — nylon is not as UV-stable as polyester, so use SunStop or PremoBond for full-sun outdoor work.",
      "Available in Tex 70, Tex 90, Tex 135 and Tex 210 in a full color range.",
    ],
    features: [
      "Bonded nylon — high tensile strength",
      "Excellent abrasion resistance",
      "Best for leather, heavy upholstery, footwear",
      "Available in multiple weights (Tex 70 / 90 / 135 / 210)",
      "Indoor / shaded use recommended",
    ],
    specs: [
      { label: "Product Category", value: "Bonded Nylon Thread" },
      { label: "Sizes", value: "Tex 70, 90, 135, 210" },
      { label: "Put Up", value: "Spool" },
      { label: "Manufacturer", value: "American & Efird (A&E)" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: ["White", "Black", "Brown", "Tan", "Navy", "Burgundy"],
    sizes: ["Tex 70", "Tex 90", "Tex 135", "Tex 210"],
    sampleVariants: [],
    productNote: "Tell us Tex size + color in your order notes.",
  },
  {
    id: "ae-sunstop-polyester",
    slug: "ae-sunstop-twisted-polyester-thread",
    name: "A&E SunStop® Twisted Non-Wick Polyester Thread",
    size: "Multiple Tex sizes · Spool",
    itemNumber: "AE-SUNSTOP",
    manufacturer: "American & Efird (A&E)",
    blurb:
      "Premium UV-stable twisted polyester — outlasts standard thread on awnings and marine canvas.",
    description: [
      "A&E SunStop® is a twisted, non-wick polyester engineered specifically for outdoor exposure. Higher UV resistance than standard bonded polyester — the thread of choice when seam life needs to match the fabric (Sunbrella®, Top Gun®).",
      "Used on premium awnings, marine canvas, outdoor upholstery and any project where the seams need to outlast the warranty period of the fabric.",
      "Available in 28+ colors and in multiple Tex sizes. Bobbins available in matching colors.",
    ],
    features: [
      "Twisted, non-wick polyester construction",
      "Premium UV resistance for outdoor seams",
      "Color-matched to popular awning fabrics",
      "Multiple Tex weights available",
      "Matching bobbins available",
    ],
    specs: [
      { label: "Product Category", value: "Outdoor Polyester Thread" },
      { label: "Construction", value: "Twisted, Non-Wick" },
      { label: "Sizes", value: "Tex 70, 90, 135, 210" },
      { label: "Put Up", value: "Spool / Bobbin" },
      { label: "Manufacturer", value: "American & Efird (A&E)" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: [
      "White",
      "Black",
      "Navy",
      "Forest Green",
      "Burgundy",
      "Beige",
      "Brown",
      "Sand",
      "Charcoal",
      "Red",
    ],
    sizes: ["Tex 70", "Tex 90", "Tex 135", "Tex 210"],
    sampleVariants: [],
    productNote: "Premium outdoor thread — best match for Sunbrella® work.",
  },
  {
    id: "coats-ultra-dee-polyester",
    slug: "coats-ultra-dee-polyester-thread",
    name: "Coats® Ultra Dee® Polyester Thread",
    size: "Multiple Tex sizes · Spool",
    itemNumber: "COATS-ULTRA-DEE",
    manufacturer: "Coats",
    blurb:
      "Spun-bonded polyester from Coats® — versatile general-purpose industrial thread.",
    description: [
      "Coats® Ultra Dee® is a continuous-filament polyester thread used widely in upholstery, marine, awning and general industrial sewing. A reliable, well-priced workhorse for shops that need consistent thread performance across many fabric types.",
      "Good UV resistance and color-fastness, smooth feed through industrial machines, and a broad color range.",
    ],
    features: [
      "Continuous-filament polyester",
      "Versatile across upholstery, marine, awning",
      "Good UV resistance, color-fast",
      "Broad color range",
      "Widely available globally",
    ],
    specs: [
      { label: "Product Category", value: "Polyester Industrial Thread" },
      { label: "Sizes", value: "Tex 70, 90, 135" },
      { label: "Put Up", value: "Spool" },
      { label: "Manufacturer", value: "Coats" },
    ],
    image: PRODUCT_PLACEHOLDERS.thread,
    colors: ["White", "Black", "Navy", "Beige", "Brown", "Forest Green"],
    sizes: ["Tex 70", "Tex 90", "Tex 135"],
    sampleVariants: [],
  },
];

export function getThreadBySlug(slug: string): ThreadProduct | undefined {
  return threadProducts.find((p) => p.slug === slug);
}
